import { useEffect, useRef, useState } from "react";
import { Button, Modal, NoticeBox } from "../ui";

const TEXT = {
  en: { title: "Confirm action", confirm: "Confirm", cancel: "Cancel", failed: "The change could not be saved. Please retry.", save: "Save these changes? They will replace the current content used by Copilot.", status: "Change the conversation status? This updates the support queue and may notify the customer.", toggle: "Change this setting? This changes which Copilot replies or notifications are available.", reorder: "Change the FAQ order? Customers will see questions in the new order.", archive: "Archive this saved reply? Agents will no longer be able to select it. Existing messages are preserved.", insert: "Insert this saved reply? Its usage count will increase and its text will be added to your draft. No message will be sent." },
  ar: { title: "تأكيد الإجراء", confirm: "تأكيد", cancel: "إلغاء", failed: "تعذر حفظ التغيير. يرجى المحاولة مجدداً.", save: "حفظ هذه التغييرات؟ ستحل محل المحتوى الحالي المستخدم في كوبيلوت.", status: "تغيير حالة المحادثة؟ سيتم تحديث قائمة الدعم وقد يتم إشعار العميل.", toggle: "تغيير هذا الإعداد؟ سيؤثر ذلك على الردود أو الإشعارات المتاحة في كوبيلوت.", reorder: "تغيير ترتيب الأسئلة الشائعة؟ ستظهر الأسئلة للعملاء بالترتيب الجديد.", archive: "أرشفة هذا الرد المحفوظ؟ لن يتمكن الموظفون من اختياره. ستبقى الرسائل السابقة محفوظة.", insert: "إدراج هذا الرد المحفوظ؟ سيزداد عدد استخداماته ويضاف النص إلى المسودة دون إرسال رسالة." },
};

// Keep the requested operation until it succeeds, including its original payload.
export function useConfirmedAction(lang) {
  const text = {
    ...(TEXT[lang] || TEXT.en),
    create: lang === "ar" ? "إنشاء هذا المحتوى؟ سيصبح متاحاً في كوبيلوت وفق الإعدادات المحددة." : "Create this entry? It will become available in Copilot according to the selected settings.",
    reset: lang === "ar" ? "استعادة الرد الافتراضي؟ سيتم استبدال النص المخصص بالرد الافتراضي للمحادثات الجديدة فقط." : "Restore the default reply? The custom text will be replaced with the default for new conversations only.",
  };
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const active = useRef(null);
  const lock = useRef(false);
  useEffect(() => () => active.current?.resolve(false), []);
  const ask = (action, kind = "save", title, danger = false, description) => {
    if (active.current) return Promise.resolve(false);
    return new Promise((resolve) => {
      const next = { action, resolve, kind, title, danger, description };
      active.current = next;
      setError(null);
      setRequest(next);
    });
  };
  const close = (result) => {
    active.current?.resolve(result);
    active.current = null;
    setRequest(null);
  };
  const execute = async () => {
    if (lock.current || !active.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      if (await active.current.action() === false) setError(text.failed);
      else close(true);
    } catch (failure) {
      setError(text.failed);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const dialog = request && (
    <Modal size="sm" role="alertdialog" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang} title={request.title || text.title}
      description={request.description || text[request.kind]} busy={busy} onClose={() => close(false)}
      footer={<><Button variant="neutral" appearance="outline" disabled={busy} onClick={() => close(false)}>{text.cancel}</Button><Button variant={request.danger ? "danger" : "primary"} appearance="filled" loading={busy} disabled={busy} onClick={execute}>{text.confirm}</Button></>}>
      {request.danger && <NoticeBox variant="warning">{request.description || text[request.kind]}</NoticeBox>}
      {error && <div role="alert"><NoticeBox variant="danger">{error}</NoticeBox></div>}
    </Modal>
  );
  return { ask, dialog };
}
